import React, { FC } from 'react';
import {
  Text,
  Flex,
  Modal,
  Button,
  Heading,
  ModalBody,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  TabPanels,
  Divider,
  Grid,
} from '@chakra-ui/react';
import { MdCalendarToday, MdFileOpen, MdLocalActivity } from 'react-icons/md';
import { Form, Formik } from 'formik';
import { EditHarvestSummaryModalViewProps } from './EditHarvestSummaryModal.props';
import FormikTextArea from 'components/primitives/FormikTextArea';
import { validationSchema } from './EditHarvestSummaryModal.utils';
import SkuDetailTab from './SkuDetailTab';
import InvoicingTab from './InvoicingTab';
import ActivityHistoryTab from './ActivityHistoryTab';
import FormikSwitch from 'components/primitives/FormikSwitch';
import { useEditHarvestSummaryModal } from './EditHarvestSummaryModalView.helpers';
import { convertUTCToLocal } from 'utils/dateUtils';
import { InventoryReportStatusEnum } from 'types/report';
import HarvestSummaryPdfTemplate from 'components/pdfs/HarvestSummaryPdfTemplate';
import CopyAsCSv from 'components/primitives/CopyAsCSv';
import { calculateLost } from 'utils/calculateLost';
import { calculateValues, returnRate, returnRateEach } from './utils';
import ImpactReportPdf from 'components/pdfs/ImpactReportPdf';
import HarvestSummaryPdfGenerator from 'routes/portal/HarvestSummary/HarvestSummaryPdfGenerator';
import { EditHarvestSummaryFormModal } from 'components/modules/EditHarvestSummaryFormModal';

const tabProps = {
  _selected: { bg: 'surface.400' },
  border: '1px solid',
  borderColor: ' #D8EBDF',
  borderRadius: '10px 10px 0 0',
  fontWeight: '600',
  fontSize: '16px',
};

const EditHarvestSummaryModalView: FC<EditHarvestSummaryModalViewProps> = (
  props,
) => {
  const {
    isOpen,
    report,
    onClose,
    onSubmit,
    isLoading,
    onRefresh,
    onDelete,
    updateStatus,
  } = props;

  const editHarvestSummaryFormModalRef = React.useRef<any>(null);

  const {
    tabIndex,
    handleChangeTab,
    getInitialValues,
    INVOINCE_TAB,
    ACTIVITY_HISTORY_TAB,
  } = useEditHarvestSummaryModal(report);

  const details = report?.details || [];
  let csvData: (string | number)[][] = [];

  const values = calculateValues(details);

  const formatCsvData = () => {
    const headers = [
      'SKU',
      'Beginning Inventory',
      'Delivered',
      'Returned Dirty',
      'Returned Unused',
      'Ending Inventory',
      'Lost',
      'Used',
      'Return Rate',
    ];

    const dataRows = details
      .sort((a, b) => a.sku?.name.localeCompare(b.sku?.name))
      .map((item) => {
        const lost = calculateLost(item);
        const used = item.ret + lost;
        const returnRate = returnRateEach(item) + '%';

        return [
          item.sku?.name || '',
          item.beg || 0,
          item.del || 0,
          item.ret || 0,
          item.unu || 0,
          item.end || 0,
          lost,
          used,
          returnRate,
        ];
      });

    const lastRow = [
      'Total Items',
      values.beginning || 0,
      values.totalDelivered || 0,
      values.returnedDirty || 0,
      values.returnedUnused || 0,
      values.ending || 0,
      values.lost || 0,
      values.used || 0,
      returnRate(values) + '%',
    ];

    return [headers, ...dataRows, lastRow];
  };

  csvData = formatCsvData();

  if (!report) {
    return null;
  }

  return (
    <Modal
      size="6xl"
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent p="0px" borderRadius="15px" minH="85vh">
        <Flex
          px="50px"
          shadow="lg"
          position="relative"
          justify="space-between"
          align="center">
          <Heading fontSize="24px" fontWeight="600" my="20px">
            Harvest Summary
          </Heading>
          <Flex>
            <Button
              mr="4"
              colorScheme="orange"
              background="orange"
              onClick={() =>
                editHarvestSummaryFormModalRef?.current?.onOpen?.(report)
              }>
              Edit
            </Button>
            <Button
              mr="4"
              colorScheme="red"
              background="red"
              onClick={onDelete}>
              Delete
            </Button>
            <Button onClick={onRefresh} mr="4">
              Refresh
            </Button>
            <HarvestSummaryPdfGenerator
              fileName={`Reuse Report Summary - ${report?.site?.name} - ${report?.name}.pdf`}
              documentTemplate={HarvestSummaryPdfTemplate}
              data={report}
              secondFileName={`Impact Report - ${report?.site?.name} - ${report?.name}.pdf`}
              secondDocumentTemplate={ImpactReportPdf}
              type="button"
            />
          </Flex>
          <ModalCloseButton _focus={{ outline: 'none' }} top="4" />
        </Flex>

        <ModalBody p="0" w="100%" alignSelf="center">
          <Flex marginX="10%" direction="column" h="200px" gap="20px">
            <Flex
              mt="20px"
              width="70%"
              justifyContent="space-around"
              alignSelf="center"
              align="center">
              <Flex direction="column" fontSize="16px" mr="10px">
                <Flex align="center" gap="5px">
                  <MdLocalActivity size="22px" color="#834EF3" />
                  <Text fontWeight="600">Site</Text>
                </Flex>
                <Text
                  title={report?.site?.name}
                  width={'12rem'}
                  flex={0.6}
                  color="gray.600"
                  style={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                    textOverflow: 'ellipsis',
                  }}>
                  {report?.site?.name}
                </Text>
              </Flex>

              <Flex direction="column" fontSize="16px">
                <Flex align="center" gap="5px">
                  <MdFileOpen size="22px" color="#D7A932" />
                  <Text fontWeight="600">Report Name</Text>
                </Flex>
                <Text
                  title={report?.name || ''}
                  width={'12rem'}
                  style={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                    textOverflow: 'ellipsis',
                  }}>
                  {report?.name || ''}
                </Text>
              </Flex>

              <Flex direction="column" fontSize="16px">
                <Flex align="center" gap="5px">
                  <MdCalendarToday size="22px" color="primary.400" />
                  <Text fontWeight="600">Report Dates</Text>
                </Flex>
                <Text>
                  {convertUTCToLocal(report?.startDate, true, true) || ''} -{' '}
                  {convertUTCToLocal(report.endDate, true, true) || ''}
                </Text>
              </Flex>
            </Flex>

            <Flex flex={1} direction="column">
              <Formik
                initialValues={getInitialValues(report)}
                validationSchema={validationSchema}
                onSubmit={onSubmit}>
                {({ values, isValid, setFieldValue }) => {
                  return (
                    <Form>
                      <Tabs
                        size="md"
                        colorScheme="primary"
                        variant="enclosed-colored"
                        onChange={handleChangeTab}
                        index={tabIndex}>
                        <TabList>
                          <Tab marginLeft="5px" {...tabProps}>
                            SKU Detail
                          </Tab>
                          <Tab marginLeft="5px" {...tabProps}>
                            Invoicing
                          </Tab>
                          <Tab marginLeft="5px" {...tabProps}>
                            Activity History
                          </Tab>
                        </TabList>
                        <TabPanels>
                          <TabPanel p="0" shadow="none">
                            <SkuDetailTab report={report} />
                          </TabPanel>
                          <TabPanel p="0" shadow="none">
                            <InvoicingTab
                              report={report}
                              isLoading={isLoading}
                              updateStatus={updateStatus}
                              setFieldValue={setFieldValue}
                            />
                          </TabPanel>
                          <TabPanel p="0" shadow="none">
                            <ActivityHistoryTab report={report} />
                          </TabPanel>
                        </TabPanels>
                      </Tabs>
                      {tabIndex !== INVOINCE_TAB &&
                        tabIndex !== ACTIVITY_HISTORY_TAB && (
                          <Flex alignItems="flex-start" flexDir="column">
                            <FormikTextArea
                              name="notes"
                              label="Harvest Summary Notes"
                              value={values.notes || ''}
                            />
                          </Flex>
                        )}
                      <Flex
                        flexDir="column"
                        pt="16px"
                        maxW="80%"
                        w="100%"
                        alignSelf="center"
                        mb="2rem">
                        <Flex align="center" gap="10px">
                          <FormikSwitch
                            name="reportReady"
                            colorScheme="primary"
                            toggleSwitch={values.reportReady}
                            onChange={(isChecked) => {
                              setFieldValue('reportReady', isChecked);
                            }}
                            readOnly={
                              report.status !==
                              InventoryReportStatusEnum.IN_REVIEW
                            }
                          />
                          <Text>Report is reviewed and ready</Text>
                        </Flex>
                        <Flex align="center" gap="10px" mt="10px">
                          <FormikSwitch
                            name="isSentToClient"
                            colorScheme="primary"
                            toggleSwitch={values.isSentToClient}
                            onChange={(isChecked) => {
                              setFieldValue('isSentToClient', isChecked);
                            }}
                          />
                          <Text>Report sent to customer</Text>
                        </Flex>
                      </Flex>
                      <Grid my={12} templateColumns="repeat(3, 1fr)" gap="10px">
                        <Flex>
                          <CopyAsCSv data={csvData} title="Copy With Format" />
                        </Flex>
                        <Flex flexDirection="row" justifyContent="center">
                          <Button
                            w="134px"
                            onClick={onClose}
                            isDisabled={isLoading}
                            variant="outline">
                            Cancel
                          </Button>
                          <Divider w="10px" />
                          <Button
                            type="submit"
                            w="134px"
                            isLoading={isLoading}
                            isDisabled={!isValid}>
                            Save
                          </Button>
                        </Flex>
                      </Grid>
                    </Form>
                  );
                }}
              </Formik>
            </Flex>
          </Flex>
        </ModalBody>
      </ModalContent>
      <EditHarvestSummaryFormModal
        ref={editHarvestSummaryFormModalRef}
        onParentClose={onClose}
      />
    </Modal>
  );
};

export default EditHarvestSummaryModalView;
